/**
 * Admin: configure the office wall-display URL. Sets the public slug used
 * at /wall/:slug. Rotate the slug any time to revoke a TV's access.
 */
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tv, Copy, ExternalLink, Save, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function WallDisplayAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState({ slug: "", updated_at: null, updated_by: null });
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/wall-display/config");
      setCfg(r.data || {});
      setDraft(r.data?.slug || "");
    } catch (e) {
      toast.error("Could not load wall config");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const save = async () => {
    const s = (draft || "").trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{2,31}$/.test(s)) {
      return toast.error("Slug must be 3–32 chars, lowercase letters / digits / dashes");
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/wall-display/config", { slug: s });
      setCfg(r.data);
      setDraft(r.data.slug);
      toast.success("Wall display URL updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const suggestSlug = () => {
    // Short random 8-char slug rooted with "xentra-" — rotates to revoke.
    const rand = Math.random().toString(36).slice(2, 10);
    setDraft(`xentra-${rand}`);
  };

  const publicUrl = cfg.slug ? `${window.location.origin}/wall/${cfg.slug}` : null;

  if (!isAdmin) {
    return (
      <Card className="p-12 text-center border-slate-200" data-testid="wall-admin-forbidden">
        <Lock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <div className="font-heading text-lg text-slate-700">Admin access required</div>
        <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          Only admins can configure the office wall display.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="wall-admin-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Admin</div>
        <div className="flex items-center gap-3 mt-1">
          <Tv className="h-6 w-6 text-blue-600" />
          <h1 className="font-heading text-3xl font-bold text-slate-900">Office wall display</h1>
        </div>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          A rolling 5-slide dashboard designed for office screens — Sales Targets · Leads · Top 10 Deals · Projects · Support.
          Auto-refreshes every 60 seconds and rotates every 30 seconds. Point any TV browser at the URL below —
          no login required. Rotate the slug at any time to revoke access.
        </p>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-sm font-heading">Bespoke URL</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-mono">Current URL</Label>
            {cfg.slug ? (
              <div className="flex items-center gap-2 flex-wrap">
                <code className="flex-1 min-w-[300px] bg-slate-900 text-emerald-300 px-4 py-3 rounded font-mono text-sm break-all" data-testid="wall-current-url">
                  {publicUrl}
                </code>
                <Button
                  variant="outline"
                  onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }}
                  data-testid="wall-copy-btn"
                >
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
                <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="wall-open-btn">
                  <Button variant="outline">
                    <ExternalLink className="h-4 w-4 mr-1" /> Open
                  </Button>
                </a>
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic">Not configured yet — set a slug below.</div>
            )}
            {cfg.updated_at && (
              <p className="text-[11px] text-slate-400 font-mono">
                Last updated {new Date(cfg.updated_at).toLocaleString()} by {cfg.updated_by || "—"}
              </p>
            )}
          </div>

          <div className="border-t border-slate-100 pt-5 space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-mono">Slug</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 font-mono">/wall/</span>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="e.g. xentra-office"
                className="max-w-sm"
                data-testid="wall-slug-input"
              />
              <Button variant="ghost" size="sm" onClick={suggestSlug} data-testid="wall-suggest-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Suggest
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              3–32 characters · lowercase letters, digits and dashes. Change this to instantly revoke any TV pointed at the old URL.
            </p>
            <div className="pt-2">
              <Button onClick={save} disabled={saving || loading} data-testid="wall-save-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-6 text-sm text-slate-600 space-y-2">
          <div className="font-semibold text-slate-800">On the TV</div>
          <ol className="list-decimal ml-5 space-y-1">
            <li>Open the built-in browser</li>
            <li>Type the URL above (or set it as the homepage / bookmark)</li>
            <li>Full-screen it — the display is designed for 1920×1080 landscape</li>
          </ol>
          <div className="text-xs text-slate-500 pt-2">
            Everything auto-refreshes. The five slides rotate on their own. No keyboard or mouse needed after setup.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
